/**
 * Built-in `viewer-snapshot` widget.
 *
 * Wraps the per-cell snapshot-loading flow that used to live in
 * `apps/dashboard/src/main.ts::loadFileIntoCell`. The widget owns a Viewer +
 * a per-cell PluginHost; on drop or `applyState` it loads the snapshot JSON.
 *
 * Snapshot storage: once a snapshot is loaded (dropped file or fetched URL)
 * the raw JSON is embedded into the layout via `ctx.addResource` and the
 * cell stores `{ snapshotRef }` only. That way the layout JSON is fully
 * self-contained — reloading the dashboard does not need to re-fetch the
 * snapshot URL. Legacy layouts that stored `snapshotUrl` still work; we
 * fetch once, embed into resources, and proceed.
 */
import {
  Viewer,
  VIEWER_CORE_VERSION,
  applySnapshot,
  healSnapshotUrls,
  type SnapshotPluginRef,
  applyUiState,
} from '@ais/viewer-core';
import {
  ControlBar,
  IconToolbar,
  InfoBar,
  Popup,
  SceneBar,
  SceneTreePanel,
  Sidebar,
  SplitView,
  ViewportOverlay,
} from '@ais/viewer-ui';

// Same build seam as ../../vendor.ts — INCLUDING the trailing-slash strip:
// the dashboard's vite config defines __SERVER_BASE__ on EVERY build,
// defaulting to '/' (NOT undefined) on standalone — so a bare length check
// would flag cccai as a site build and fire live fetches there. After the
// strip, default '/' → '' (not a site) and base-portable './' → '.' (site).
// Live checkpoint fetching is a SITE feature; default builds keep the
// embedded-copy behaviour byte-identically (fallback contract).
declare const __SERVER_BASE__: string | undefined;
const SITE_BUILD =
  typeof __SERVER_BASE__ === 'string' && __SERVER_BASE__.replace(/\/$/, '').length > 0;
import { PluginHost } from '@ais/plugin-host';
import type {
  DashboardWidget,
  DashboardWidgetInstance,
  DashboardCtx,
  CellPlugin,
  CellResult,
  CellRun,
  ResourceRef,
} from '../../Widget';
import { isResourceRef } from '../../Widget';

/**
 * Which bars this cell mounts.
 *
 * VISIBILITY only — the sidebar's CONTENTS come from the loaded scene (its
 * baked item set), not from here, so one setup serves a standalone reload and
 * a cell alike. Absent means today's behaviour: a bare preview with no chrome.
 *
 * The toolbar implies the ControlBar; there is deliberately no way to
 * configure a cell whose section box has no Inside/Outside strip. The control
 * bar can also be worn on its own (`controlbar`), for the strips plugins add
 * (e.g. Quality check's level arrows) in a cell with no toolbar.
 *
 * Lives on the VIEWER widget's config, not on `DashboardCell` — a CCTV or
 * chart cell has no bars and must not inherit a field about them.
 */
export interface ViewerCellUi {
  /** Asset sections. Its items come from the scene's APP (`snapshot.app` —
   *  the app manifest's `ui`, so a Model Comparison scene shows Model
   *  Comparison's surface) with the scene's own `ui` on top; a scene without
   *  an app shows every section. */
  sidebar?: boolean;
  /** Tool buttons. Brings the ControlBar (tool control strips) with it. */
  toolbar?: boolean;
  /** Scene tree panel (left, next to the sidebar). */
  sceneTree?: boolean;
  /** Info history (right) — measurement rows, plugin reports. */
  infobar?: boolean;
  /** Bottom-right scene-view buttons (fullscreen, zoom, standard views). */
  scenebar?: boolean;
  /** Bottom-centre control strips (tool options, plugin strips) without the
   *  toolbar; the toolbar always brings it. */
  controlbar?: boolean;
  /** Whether each worn bar starts OPEN (true) or tucked away (false) when the
   *  cell loads a scene — applied after the scene, so it wins over the state a
   *  save (or its app) carries. Absent key = as the scene left it. */
  open?: Partial<Record<CellBarKey, boolean>>;
  /** Timeline scrubber for a FOLLOWED checkpoint (site builds): step through
   *  the source track's scans via the resolver's `?at=`. Renders only when the
   *  checkpoint actually follows a track — ticking it on an unfollowed cell
   *  shows nothing. */
  timeline?: boolean;
}

/** The bars that can start open or tucked away. */
export type CellBarKey = 'sidebar' | 'toolbar' | 'sceneTree' | 'infobar' | 'scenebar';
const OPENABLE: CellBarKey[] = ['sidebar', 'toolbar', 'sceneTree', 'infobar', 'scenebar'];

export interface ViewerSnapshotConfig {
  /** Ref into layout.resources (preferred). */
  snapshotRef?: ResourceRef;
  /** Legacy: URL the snapshot was originally loaded from. Still supported on load. */
  snapshotUrl?: string;
  /** Bars this cell shows. Absent = none, i.e. the read-only preview. */
  ui?: ViewerCellUi;
}

interface ViewerSnapshotState {
  snapshotRef?: ResourceRef;
  snapshotUrl?: string;
  snapshotName?: string | null;
  /** Pinned timeline position (epoch seconds). Persisted ON PURPOSE: a wall
   *  display configured to show "the site as of <date>" must survive a board
   *  reload. Absent = live (the resolver serves the track's latest). */
  timelineAt?: number | null;
}

class ViewerSnapshotInstance implements DashboardWidgetInstance {
  private viewer: Viewer | null = null;
  private pluginHost: PluginHost | null = null;
  private host: HTMLElement;
  private config: ViewerSnapshotConfig;
  private ctx: DashboardCtx;
  private statusEl: HTMLElement;
  private hostEl: HTMLElement;
  private dropEl: HTMLElement;
  private currentSnapshotName: string | null = null;
  /** The checkpoint the board ASKED this cell to show, kept from applyState
   *  on — separate from `currentSnapshotName`, which only says what actually
   *  loaded. A load that fails (fetch blip, a pinned plugin the running bundle
   *  cannot install) used to leave `currentSnapshotName` null, so the next
   *  Save wrote `snapshotName: null` and the cell forgot its scene for good
   *  (full.json, 2026-09-15). The ask survives until the operator picks
   *  something else. */
  private requestedSnapshotName: string | null = null;
  private currentSnapshotUrl: string | null = null;
  /** Timeline: pinned epoch (null = live) + the strip's DOM + track versions. */
  private timelineAt: number | null = null;
  private timelineEl: HTMLElement | null = null;
  private timelineVersions: Array<{ epoch: number; door?: string; kind?: string }> = [];
  private currentSnapshotRef: ResourceRef | null = null;
  /** Engine signal unsubscribes for the cell-bus publisher. */
  private feedUnsubs: Array<() => void> = [];

  private onDragOver = (e: DragEvent): void => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    this.host.classList.add('vs-drag');
  };
  private onDragLeave = (): void => {
    this.host.classList.remove('vs-drag');
  };
  private onDrop = (e: DragEvent): void => {
    e.preventDefault();
    this.host.classList.remove('vs-drag');
    const f = e.dataTransfer?.files?.[0];
    if (f) void this.loadFile(f);
  };
  private onDropClick = (): void => this.pickFile();

  constructor(host: HTMLElement, config: ViewerSnapshotConfig, ctx: DashboardCtx) {
    this.host = host;
    this.config = config;
    this.ctx = ctx;

    host.classList.add('widget-viewer-snapshot');

    this.hostEl = document.createElement('div');
    this.hostEl.className = 'vs-host';
    host.appendChild(this.hostEl);

    this.dropEl = document.createElement('div');
    this.dropEl.className = 'vs-drop';
    const dropInner = document.createElement('div');
    dropInner.className = 'vs-drop-inner';
    dropInner.innerHTML = '<div class="vs-drop-icon">⬇</div><div>Drop snapshot JSON</div>';
    // Primary "pick a result" affordance — only when the host wired a site
    // result picker (base-portable builds). Clicking it opens the picker and
    // loads the chosen result through the SAME loadFromUrl path a drop uses.
    // Without a picker the cell keeps only the drop / click-to-browse-local
    // affordance — byte-identical to before (the fallback contract).
    if (ctx.pickResultUrl) {
      const pickBtn = document.createElement('button');
      pickBtn.type = 'button';
      pickBtn.className = 'vs-pick-btn';
      pickBtn.textContent = 'Pick a result…';
      pickBtn.addEventListener('click', (e) => {
        // Stop the click bubbling to the drop overlay (which would also open
        // the local <input type=file> dialog).
        e.stopPropagation();
        void this.pickFromSite();
      });
      dropInner.appendChild(pickBtn);
      const or = document.createElement('div');
      or.className = 'vs-drop-or';
      or.textContent = 'or drop / click to browse';
      dropInner.appendChild(or);
    }
    this.dropEl.appendChild(dropInner);
    host.appendChild(this.dropEl);

    this.statusEl = document.createElement('div');
    this.statusEl.className = 'vs-status';
    this.statusEl.textContent = 'empty';
    host.appendChild(this.statusEl);

    host.addEventListener('dragover', this.onDragOver);
    host.addEventListener('dragleave', this.onDragLeave);
    host.addEventListener('drop', this.onDrop);
    this.dropEl.addEventListener('click', this.onDropClick);

    // Initial load: prefer ref over URL.
    if (isResourceRef(this.config.snapshotRef)) {
      void this.loadFromRef(this.config.snapshotRef);
    } else if (this.config.snapshotUrl) {
      void this.loadFromUrl(this.config.snapshotUrl);
    }
  }

  private setStatus(msg: string): void {
    this.statusEl.textContent = msg;
  }

  /** Open the host's site result picker, then load the chosen result URL. */
  private async pickFromSite(): Promise<void> {
    if (!this.ctx.pickResultUrl) return;
    let url: string | null;
    try {
      url = await this.ctx.pickResultUrl();
    } catch (e) {
      this.setStatus(`pick failed: ${(e as Error).message}`);
      console.error(e);
      return;
    }
    if (!url) return; // user cancelled — no-op
    await this.loadFromUrl(url);
  }

  private pickFile(): void {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = 'application/json,.json';
    inp.addEventListener('change', () => {
      const f = inp.files?.[0];
      if (f) void this.loadFile(f);
    });
    inp.click();
  }

  private embedSnapshot(json: unknown, name: string): void {
    this.currentSnapshotRef = this.ctx.addResource({
      type: 'snapshot',
      name,
      content: json,
    });
  }

  private async loadFile(file: File): Promise<void> {
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      this.currentSnapshotName = file.name;
      this.currentSnapshotUrl = null;
      this.embedSnapshot(json, file.name);
      await this.loadSnapshot(json, file.name);
    } catch (e) {
      this.setStatus(`error: ${(e as Error).message}`);
      console.error(e);
    }
  }

  private async loadFromUrl(url: string): Promise<void> {
    try {
      this.setStatus(`fetching ${url}…`);
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      const label = url.split('/').pop() || url;
      this.currentSnapshotName = label;
      this.currentSnapshotUrl = url;
      this.embedSnapshot(json, label);
      await this.loadSnapshot(json, label);
    } catch (e) {
      this.setStatus(`error: ${(e as Error).message}`);
      console.error(e);
    }
  }

  private async loadFromRef(ref: ResourceRef): Promise<void> {
    const res = this.ctx.getResource(ref);
    if (!res || res.type !== 'snapshot' || !res.content) {
      this.setStatus(`error: missing resource ${ref.$ref}`);
      return;
    }
    const label = res.name ?? ref.$ref;
    this.currentSnapshotName = label;
    this.currentSnapshotUrl = null;
    this.currentSnapshotRef = ref;
    await this.loadSnapshot(res.content, label);
  }

  private maximised = false;
  private barsMounted = false;
  /** The loaded scene's `ui` block, kept for `recordedRuns` — the InfoBar rows
   *  it carries are the scene's exec history whether or not this cell mounts
   *  an InfoBar. (Applying it to the bars is applySnapshot's job: bars mount
   *  before the scene is applied, so it lands in a sidebar that exists.) */
  private snapshotUi: unknown = null;

  /**
   * The cell was maximised or restored.
   *
   * Chrome appears only while maximised, for two reasons that point the same
   * way: a full sidebar in a 400 px cell is unusable, and viewer-ui's bars are
   * viewport-fixed singletons keyed by id — so exactly one cell can wear them
   * at a time, and that is the one filling the board.
   *
   * Bars are mounted once and then only hidden (never disposed), so the plugin
   * panels registered into a sidebar survive a restore.
   */
  setMaximised(on: boolean): void {
    this.maximised = on;
    // Bars are per-cell and always on now; maximise is just room to work in.
    // Kept on the contract because a widget may still want to know.
  }

  /**
   * Mount the bars named in `config.ui`. Nothing named → nothing mounted,
   * which is the read-only preview every existing board expects.
   *
   * Each bar is constructed only when asked for: unlike the admin, a cell
   * cannot afford to build six adapters it will never show, and a bar that is
   * never mounted costs nothing at all.
   */
  private mountConfiguredBars(v: Viewer): void {
    const ui = this.config.ui;
    if (!ui) return;
    // Every bar is SCOPED to this cell's host: class-only elements positioned
    // inside the cell, so two cells can each have a sidebar without colliding
    // on `#sidebar` or floating over the dashboard.
    if (this.barsMounted) return;
    const container = this.hostEl;
    container.classList.add('viewer-host-scoped');
    // One try per bar: a bar that fails to mount must not take the cell's viewer
    // with it, nor the bars after it.
    const mount = (name: string, fn: () => void): void => {
      try { fn(); } catch (e) { console.warn(`[viewer-snapshot] ${name} mount failed: ${(e as Error).message}`); }
    };
    // No title override: viewer-ui's default is 'Scene' (ui >= 0.34), which is
    // exactly what a cell shows. Naming it again here would put one word in two
    // places and invite them to disagree.
    if (ui.sidebar) {
      // assetPicker: the host's site-storage browse buttons, when it has them.
      // A cell shows a scene that came FROM the site, so the editor's "add a
      // point cloud / BIM / splat from site storage" buttons have to be here
      // too — without them the cell looked like a different build of the
      // viewer. Absent capability (default build) → local-file "+" only,
      // unchanged.
      mount('sidebar', () => v.attachUI(
        new Sidebar(v, {
          container,
          showSnapshotButtons: false,
          ...(this.ctx.pickAssetUrl ? { assetPicker: (k) => this.ctx.pickAssetUrl!(k) } : {}),
        }),
        'sidebar',
      ));
    }
    // The strip a tool's controls appear in — implied by the toolbar, never
    // optional there, or an armed section box would have no Inside/Outside
    // buttons; also wearable alone for plugin strips.
    if (ui.toolbar || ui.controlbar) mount('controlbar', () => v.attachUI(new ControlBar(v, { container }), 'controlbar'));
    if (ui.toolbar) mount('toolbar', () => v.attachUI(new IconToolbar(v, { container }), 'iconbar'));
    if (ui.sceneTree) mount('sceneTree', () => v.attachUI(new SceneTreePanel(v, { container }), 'sceneTree'));
    if (ui.infobar) mount('infobar', () => v.attachUI(new InfoBar(v, { container }), 'infobar'));
    if (ui.scenebar) {
      // The bar is only a frame: its buttons (fullscreen, zoom, map style,
      // mouse scheme, standard views) are registered by the ViewportOverlay —
      // without it the cell showed an empty bar. Both scoped to the cell, the
      // bar first (the overlay adopts `viewer.ui.scenebar`).
      mount('scenebar', () => {
        v.attachUI(new SceneBar(v, { container }), 'scenebar');
        v.attachUI(new ViewportOverlay(v, { container }), 'overlay');
      });
    }
    this.barsMounted = true;
  }

  /**
   * Open or tuck away each worn bar the cell config names (`ui.open`: true =
   * open, false = tucked away; an absent key leaves the bar as the scene and
   * its app left it, so existing boards render as before). Runs after EVERY
   * scene load into the cell, after the app's and the scene's own `ui` (a save
   * made in li-gs tucks the scene bar): the board's setting wins.
   */
  private applyOpenStates(v: Viewer): void {
    const ui = this.config.ui;
    if (!ui?.open) return;
    const state: Record<string, unknown> = {};
    const set = (k: CellBarKey, worn: boolean | undefined, fn: (open: boolean) => unknown, as: string): void => {
      const o = ui.open?.[k];
      if (worn && typeof o === 'boolean') state[as] = fn(o);
    };
    set('sidebar', ui.sidebar, (o) => ({ collapsed: !o }), 'sidebar');
    set('toolbar', ui.toolbar, (o) => ({ collapsed: !o }), 'iconbar');
    // the panel stays mounted; "closed" tucks it to its edge handle
    set('sceneTree', ui.sceneTree, (o) => ({ collapsed: !o }), 'sceneTree');
    set('infobar', ui.infobar, (o) => ({ collapsed: !o }), 'infobar');
    set('scenebar', ui.scenebar, (o) => ({ collapsed: !o }), 'scenebar');
    applyUiState(v, state as Parameters<typeof applyUiState>[1], (m) => console.warn(`[viewer-snapshot ${this.ctx.cellId}] ${m}`));
  }

  /**
   * Edit-mode form: which bars this cell wears.
   *
   * The JSON was always the source of truth, but authoring a board by hand-
   * editing it is not a workflow — this is the same setting with a checkbox
   * in front of it. Toggling re-mounts the cell (the host does that), which is
   * how a bar appears or disappears immediately.
   *
   * The sidebar's CONTENTS are deliberately NOT here: they come from the scene
   * the cell is showing, so one setup serves a standalone reload and a cell.
   */
  /**
   * The cell's config form — rendered INTO the existing Configure drawer (the
   * host calls this; there is no second surface).
   *
   * The JSON was always the source of truth, but hand-editing a board is not a
   * workflow. Toggling re-mounts the cell, which is how a bar appears or
   * disappears immediately.
   *
   * The sidebar's CONTENTS are deliberately NOT here: they come from the scene
   * the cell is showing, so one setup serves a standalone reload and a cell.
   */
  renderConfigUI(host: HTMLElement, onChange: (cfg: unknown) => void): void {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:flex;flex-direction:column;gap:6px;';

    const head = document.createElement('div');
    head.textContent = 'Bars shown in this cell';
    head.style.cssText = 'font-size:11px;font-weight:600;';
    wrap.appendChild(head);

    type BarKey = Exclude<keyof ViewerCellUi, 'open'>;
    const rows: Array<[BarKey, string, string]> = [
      ['sidebar', 'Sidebar', 'Asset sections. WHICH sections appear comes from the loaded scene.'],
      ['toolbar', 'Toolbar', 'Tool buttons — brings the control bar with it.'],
      ['controlbar', 'Control bar', 'Bottom-centre strips: tool options (cut, measure) and plugin strips such as level arrows.'],
      ['sceneTree', 'Scene tree', 'The scene → layer → asset tree.'],
      ['infobar', 'Info history', 'Measurement rows and plugin reports.'],
      ['scenebar', 'Scene buttons', 'Fullscreen, zoom, map style, mouse scheme, standard views.'],
      ['timeline', 'Timeline', 'Scrub the followed source\'s scan history (followed checkpoints only). With Recalculate armed, analyses re-derive at the picked time; otherwise rows stay the latest saved run.'],
    ];
    const commit = (next: ViewerCellUi): void => {
      if (next.open && !Object.keys(next.open).length) delete next.open;
      const cfg: ViewerSnapshotConfig = { ...this.config };
      // An empty block is dropped, so a cell wearing nothing stays
      // byte-identical to one that never had the field.
      if (Object.keys(next).length) cfg.ui = next;
      else delete cfg.ui;
      onChange(cfg);
    };
    for (const [key, label, tip] of rows) {
      const line = document.createElement('div');
      line.style.cssText = 'display:flex;align-items:center;gap:10px;font-size:11px;';
      const row = document.createElement('label');
      row.title = tip;
      row.style.cssText = 'display:flex;align-items:center;gap:6px;cursor:pointer;min-width:110px;';
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      const viaToolbar = key === 'controlbar' && !!this.config.ui?.toolbar;
      cb.checked = viaToolbar || !!this.config.ui?.[key];
      cb.disabled = viaToolbar;
      if (viaToolbar) row.title = 'Comes with the toolbar.';
      cb.addEventListener('change', () => {
        const next: ViewerCellUi = { ...(this.config.ui ?? {}) };
        if (cb.checked) {
          next[key] = true;
          // a bar ticked now starts open (its "open" box ticked), not "as the scene"
          if ((OPENABLE as string[]).includes(key)) next.open = { ...(next.open ?? {}), [key]: true };
        } else {
          delete next[key];
          if (next.open && key in next.open) { next.open = { ...next.open }; delete next.open[key as CellBarKey]; }
        }
        commit(next);
      });
      row.append(cb, document.createTextNode(label));
      line.appendChild(row);
      // "Open" — whether a worn bar starts open or tucked away.
      if ((OPENABLE as string[]).includes(key) && this.config.ui?.[key]) {
        const k = key as CellBarKey;
        const o = document.createElement('label');
        const cur = this.config.ui?.open?.[k];
        o.title = cur === undefined
          ? `${label}: as the loaded scene left it. Tick to make it start open, untick to start tucked away.`
          : `${label} starts ${cur ? 'open' : 'tucked away'} when the cell loads a scene.`;
        o.style.cssText = 'display:flex;align-items:center;gap:4px;cursor:pointer;opacity:.85;';
        const ob = document.createElement('input');
        ob.type = 'checkbox';
        ob.checked = cur === true;
        ob.indeterminate = cur === undefined; // as the scene left it (boards made before 0.10)
        ob.addEventListener('change', () => {
          const next: ViewerCellUi = { ...(this.config.ui ?? {}), open: { ...(this.config.ui?.open ?? {}), [k]: ob.checked } };
          commit(next);
        });
        o.append(ob, document.createTextNode('open'));
        line.appendChild(o);
      }
      wrap.appendChild(line);
    }

    const note = document.createElement('div');
    note.style.cssText = 'font-size:10px;opacity:.7;line-height:1.4;margin-top:2px;';
    note.textContent = 'No bars = a read-only preview. Bars are presentation only — the scene, '
      + 'its tools and its plugins work either way.';
    wrap.appendChild(note);
    host.appendChild(wrap);
  }

  /**
   * Announce this cell's output on the board bus (see `CellFeed`).
   *
   * A cell runs every plugin its snapshot names, so the feed is keyed by
   * PLUGIN — which costs nothing, because the engine already stores it that
   * way (`execLog` per plugin id, `producer` on every result). A reader then
   * addresses `cell → plugin` and stays correct when the cell loads a
   * different snapshot with a different plugin set.
   */
  /**
   * Runs RECORDED IN THE SNAPSHOT rather than run in this cell.
   *
   * `viewer.execLog` is a session log — a cell that loads a checkpoint starts
   * it empty, so a wall board showing an authored scene would say "no run yet"
   * about numbers the scene plainly contains. §12 keeps exactly one serialized
   * copy of that history: the InfoBar rows (`ui.infobar.entries`, id
   * `exec:<plugin>:exec-<n>`, `data: {input, result|error}`) — the same source
   * `execReplay` reads. This harvests them whether or not the cell mounts an
   * InfoBar, because the feed is about the data, not the chrome.
   */
  private recordedRuns(): Record<string, CellRun[]> {
    const out: Record<string, CellRun[]> = {};
    const ui = this.snapshotUi as { infobar?: { entries?: unknown } } | null;
    const entries = ui?.infobar?.entries;
    if (!Array.isArray(entries)) return out;
    for (const e of entries as Array<Record<string, unknown>>) {
      const m = typeof e?.id === 'string' ? /^exec:(.+):(exec-\d+)$/.exec(e.id) : null;
      if (!m) continue;
      const d = (e.data ?? {}) as { input?: unknown; result?: unknown; error?: string };
      (out[m[1]] ??= []).push({
        id: m[2],
        time: typeof e.time === 'string' ? e.time : '',
        input: d.input,
        result: d.result,
        error: d.error,
      });
    }
    // Rows serialize newest-first; a run list reads oldest-first.
    for (const k of Object.keys(out)) out[k].reverse();
    return out;
  }

  /**
   * Announce this cell's output on the board bus (see `CellFeed`).
   *
   * A cell runs every plugin its snapshot names, so the feed is keyed by
   * PLUGIN — which costs nothing, because the engine already stores it that
   * way (`execLog` per plugin id, `producer` on every result). A reader then
   * addresses `cell → plugin` and stays correct when the cell loads a
   * different snapshot with a different plugin set.
   */
  private publishFeed(): void {
    if (!this.ctx.publish) return;
    const v = this.viewer;
    if (!v) {
      this.ctx.publish(null);
      return;
    }
    const recorded = this.recordedRuns();
    const log = v.execLog.all();

    // A live re-run reuses its run id (`exec-1` again after a reload), so the
    // session log OVERWRITES the recorded slot rather than doubling it — the
    // same convergence rule the InfoBar applies to its rows.
    const runsFor = (id: string): CellRun[] => {
      const merged = new Map<string, CellRun>();
      for (const r of recorded[id] ?? []) merged.set(r.id, r);
      for (const r of log[id] ?? []) {
        merged.set(r.id, {
          id: r.id,
          time: r.time,
          input: r.input,
          result: r.result,
          error: r.error,
        });
      }
      return [...merged.values()];
    };

    // Union of: installed plugins, plugins with a session run, plugins with a
    // recorded run. The last two matter when a plugin FAILED to install — its
    // history is still the honest record of what this scene computed, and
    // hiding it would read as "this scene never ran anything".
    const ids = new Set<string>([
      ...v.plugins.keys(),
      ...Object.keys(log),
      ...Object.keys(recorded),
    ]);
    const plugins: CellPlugin[] = [...ids].map((id) => ({
      id,
      label: v.plugins.get(id)?.label,
      runs: runsFor(id),
    }));

    const results: CellResult[] = v.results.list().map((e) => ({
      id: e.id,
      kind: e.kind,
      label: e.label,
      producer: e.producer,
      semantic: e.semantic,
    }));

    this.ctx.publish({
      title: this.currentSnapshotName ?? undefined,
      plugins,
      results,
    });
  }

  /** Re-publish whenever the engine says the numbers moved. */
  private watchForFeed(v: Viewer): void {
    this.feedUnsubs.push(v.on('execChanged', () => this.publishFeed()));
    this.feedUnsubs.push(v.on('resultsChanged', () => this.publishFeed()));
  }

  private async loadSnapshot(json: unknown, label: string): Promise<void> {
    this.clearViewer();
    this.setStatus(`applying ${label}…`);
    this.host.classList.add('loaded');

    const v = new Viewer(this.hostEl);
    this.viewer = v;
    // The site's files store, as the editor sets it (`${SBASE}/files`): add-ons
    // derive the hub's paths from it (CORE-COMPLIANCE §15) and the geodata cache
    // reads through it. Left at the '/files' default, a cell on a hub pointed
    // them at the origin root (Quality check found no site, 0.10.1).
    if (SITE_BUILD) v.filesPrefix = `${(__SERVER_BASE__ as string).replace(/\/$/, '')}/files`;

    // SplitView is the only state-bearing UI adapter that applySnapshot
    // restores; mount it so split-pane configuration round-trips.
    v.attachUI(new SplitView(v), 'splitView');
    // The cell's own info card (viewer.ui.popup) — mounted ALWAYS, not gated
    // on config.ui: it is invisible until a tool or plugin opens one, and each
    // cell's viewer needs its own (single instance per viewer, not per page).
    v.attachUI(new Popup(v), 'popup');

    // Bars mount HERE, per cell, scoped to this cell's host — every cell can
    // wear its own now (they carry classes, not the page's global ids), so
    // chrome no longer waits for a maximise.
    this.mountConfiguredBars(v);

    v.onStatus((msg) => this.setStatus(msg));
    this.watchForFeed(v);
    v.start();

    const host = new PluginHost(v, {
      engineVersion: VIEWER_CORE_VERSION,
      // Per-cell key so concurrent cells don't stomp each other's persisted lists.
      storageKey: `@ais/plugin-host:dashboard:${this.ctx.cellId}`,
      // Same seam as the admin and the widget host: on a site build the cell reads
      // its HOST's mirror. Left at the default it hit the origin root, which 403s
      // on a data hub — harmless only because snapshots carry each plugin's own
      // manifestUrl, so an id-only reference had nothing to resolve against.
      ...(SITE_BUILD ? { registryUrl: `${__SERVER_BASE__.replace(/\/$/, '')}/plugins/registry.json` } : {}),
    });
    this.pluginHost = host;

    // Install the plugins the snapshot relies on before applySnapshot. Threads
    // the serialization MARK (`snapshot.plugins`) alongside the extensions STATE
    // so a snapshot saved on another site re-resolves each plugin from its
    // recorded manifestUrl, falling back to a registry id-lookup. Absent mark ⇒
    // undefined ⇒ byte-identical to the pre-mark install path.
    // §15: pin-free URLs BEFORE mark resolution too — a mark's manifestUrl
    // saved against the box's direct hostname must re-resolve through the
    // portal's domain, same as the asset urls applySnapshot heals.
    json = healSnapshotUrls(json);
    const obj = json && typeof json === 'object' ? (json as Record<string, unknown>) : undefined;
    this.snapshotUi = obj?.ui ?? null;
    const exts = obj?.extensions as Record<string, unknown> | undefined;
    let marks = Array.isArray(obj?.plugins)
      ? (obj!.plugins as SnapshotPluginRef[])
      : undefined;
    // The '@ais/registry' MARKER (appended by the editor's checkpoint save)
    // records WHICH store the plugins came from. Re-point this cell's host at
    // it BEFORE resolving the marks — an editor checkpoint dropped/picked into
    // a dashboard cell then auto-downloads its plugins from the same store
    // (e.g. the fleet marketplace), regardless of this app's default. The
    // marker itself is stripped so it is never resolved as a plugin id.
    const regMark = marks?.find((m) => m.id === '@ais/registry' && m.registryUrl);
    // Same rule as the admin's: on a SITE build the host's own mirror wins. A cell
    // that followed the marker would fetch plugins from a remote store while the
    // hub holds them locally — the exact dependency the mirror exists to remove,
    // and the reason a board would go blank the moment the uplink drops.
    if (regMark?.registryUrl && !SITE_BUILD) host.setRegistryUrl(regMark.registryUrl);
    marks = marks?.filter((m) => m.id !== '@ais/registry');
    if (marks && marks.length === 0) marks = undefined;
    // A scene saved from an APP door (`snapshot.app`) is that app's scene: the
    // cell installs the app + its `dependsOn` and wears the app's `ui` — not
    // the 17 marks the editor's full install pile stamped into `plugins`.
    // Marks still pin the dependency versions. The scene's own `ui` lands on
    // top inside applySnapshot (hidden/collapsed state; never `sections`).
    // No app, or the app cannot be resolved → the mark path, unchanged.
    const app = obj?.app as SnapshotPluginRef | undefined;
    let appDone = false;
    if (app && typeof app === 'object' && typeof app.id === 'string') {
      try {
        const r = await host.installApp(app, marks);
        appDone = true;
        console.info(
          `[viewer-snapshot ${this.ctx.cellId}] app ${app.id}: installed ${r.installed.join(', ')}`,
        );
        if (r.missing.length > 0) {
          console.warn(
            `[viewer-snapshot ${this.ctx.cellId}] app ${app.id} needs unknown/incompatible plugins: ${r.missing.join(', ')}`,
          );
        }
        if (r.manifest.ui) {
          applyUiState(v, r.manifest.ui, (m) => console.warn(`[viewer-snapshot ${this.ctx.cellId}] ${m}`));
        }
      } catch (e) {
        console.warn(
          `[viewer-snapshot ${this.ctx.cellId}] app ${app.id} unavailable, opening as a plain scene: ${(e as Error).message}`,
        );
      }
    }
    if (!appDone && ((exts && typeof exts === 'object') || marks)) {
      const result = await host.installForSnapshot(
        exts && typeof exts === 'object' ? exts : {},
        marks,
      );
      if (result.installed.length > 0) {
        console.info(
          `[viewer-snapshot ${this.ctx.cellId}] installed plugins: ${result.installed.join(', ')}`,
        );
      }
      if (result.missing.length > 0) {
        console.warn(
          `[viewer-snapshot ${this.ctx.cellId}] snapshot references unknown/incompatible plugins: ${result.missing.join(', ')}`,
        );
      }
    }

    await applySnapshot(v, json, {
      onWarn: (m) => console.warn(`[viewer-snapshot ${this.ctx.cellId}]`, m),
    });
    this.applyOpenStates(v);
    this.setStatus(`loaded · ${label}`);
    // The scene is up and its plugins are installed — tell the board, so a
    // reader cell mounted earlier stops saying "no source" and shows numbers.
    this.publishFeed();
  }

  private clearViewer(): void {
    for (const off of this.feedUnsubs) {
      try {
        off();
      } catch (e) {
        console.warn(e);
      }
    }
    this.feedUnsubs = [];
    // Withdraw before disposal: a reader pointed here must fall back to "source
    // has no scene" rather than keep painting the outgoing snapshot's numbers.
    this.ctx.publish?.(null);
    if (this.viewer) {
      try {
        this.viewer.dispose();
      } catch (e) {
        console.error(e);
      }
      this.viewer = null;
    }
    this.pluginHost = null;
    this.hostEl.innerHTML = '';
    // The bars lived in that markup and belonged to the disposed viewer —
    // forget them, or a second snapshot into this cell would come up bare.
    this.barsMounted = false;
    this.host.classList.remove('loaded');
  }

  /** State persists the resource ref so the snapshot survives reload. */
  getState(): ViewerSnapshotState {
    return {
      snapshotRef: this.currentSnapshotRef ?? this.config.snapshotRef,
      snapshotUrl: this.currentSnapshotUrl ?? this.config.snapshotUrl,
      // What loaded, else what was asked for: a failed load is not a reason
      // to save the cell as empty.
      snapshotName: this.currentSnapshotName ?? this.requestedSnapshotName,
      ...(this.timelineAt ? { timelineAt: this.timelineAt } : {}),
    };
  }

  async applyState(s: unknown): Promise<void> {
    if (!s || typeof s !== 'object') return;
    const state = s as ViewerSnapshotState;
    // LIVE-SOURCE (mid-server): a NAMED checkpoint is fetched live from the
    // site store first — the datahub serves it with followed sources resolved
    // (resolve-on-serve), so a dashboard cell tracks the latest scan exactly
    // like the Editor does. Without this, cells rendered the resource copy
    // embedded at drop time and never saw newer scans. Relative URL on
    // purpose: it resolves under any mount and hits the front's
    // GET-of-checkpoints resolver route. The embedded copy stays the
    // offline/standalone fallback, so bare builds behave as before.
    if (state.snapshotName) this.requestedSnapshotName = state.snapshotName;
    if (state.snapshotName && SITE_BUILD) {
      try {
        this.timelineAt = typeof state.timelineAt === 'number' ? state.timelineAt : null;
        const base = `files/checkpoints/${encodeURIComponent(state.snapshotName)}`;
        const url = this.timelineAt ? `${base}?at=${this.timelineAt}` : base;
        const r = await fetch(url, { cache: 'no-store' });
        if (r.ok) {
          const json = await r.json();
          await this.loadSnapshot(json, state.snapshotName);
          // Only a load that finished names the cell "current".
          this.currentSnapshotName = state.snapshotName;
          this.currentSnapshotUrl = null;
          void this.renderTimeline();
          return;
        }
        this.setStatus(`could not load ${state.snapshotName}: HTTP ${r.status} — the cell keeps it; reload to retry`);
        if (!isResourceRef(state.snapshotRef) && !state.snapshotUrl) return;
      } catch (e) {
        // Say why, and KEEP the name: an embedded copy (if any) is a fallback,
        // not a replacement for what the board asked for.
        this.setStatus(`could not load ${state.snapshotName}: ${(e as Error).message} — the cell keeps it; reload to retry`);
        if (!isResourceRef(state.snapshotRef) && !state.snapshotUrl) return;
      }
    }
    if (isResourceRef(state.snapshotRef)) {
      await this.loadFromRef(state.snapshotRef);
    } else if (state.snapshotUrl) {
      await this.loadFromUrl(state.snapshotUrl);
    }
  }

  /**
   * The timeline strip — time-travel for a FOLLOWED checkpoint (site builds).
   *
   * The data mechanism is entirely the hub\'s: the sidecar names the followed
   * track, the track API lists its scans, and the resolver serves the
   * checkpoint AS OF any epoch via `?at=` — pinned opens never recompute (the
   * recalc flag is only injected for live "moved" serves). The strip is just a
   * face on those three endpoints. Scene rows time-travel; the checkpoint\'s
   * saved result rows do NOT (they are whatever run was last saved), and the
   * strip\'s tooltip says so rather than letting the mismatch surprise anyone.
   */
  private async renderTimeline(): Promise<void> {
    this.timelineEl?.remove();
    this.timelineEl = null;
    if (!SITE_BUILD || !this.config.ui?.timeline || !this.currentSnapshotName) return;
    try {
      if (this.timelineVersions.length === 0) {
        const meta = await fetch(`../api/checkpoints/${encodeURIComponent(this.currentSnapshotName)}/meta`,
          { cache: 'no-store' }).then((r) => (r.ok ? r.json() : null));
        const follow = (meta?.follow ?? {}) as Record<string, string>;
        const track = Object.values(follow)[0];
        if (!track) return;                       // not followed — nothing to scrub
        const key = String(track).split('/')[0];
        const t = await fetch(`../api/models/tracks/${encodeURIComponent(key)}?order=asc&limit=500`,
          { cache: 'no-store' }).then((r) => (r.ok ? r.json() : null));
        const vs = (t?.versions ?? []) as Array<{ epoch: number; door?: string; kind?: string }>;
        if (vs.length < 2) return;                // one scan = no line to walk
        this.timelineVersions = vs;
      }
      const vs = this.timelineVersions;
      const strip = document.createElement('div');
      strip.className = 'vs-timeline';
      strip.title = 'Scene as of the picked scan. With Recalculate armed on this checkpoint, '
        + 'analyses re-derive live at that time (a recompute per scrub, nothing saved back); '
        + 'otherwise result rows show the latest saved run.';
      const idx = this.timelineAt
        ? Math.max(0, vs.findIndex((v) => v.epoch === this.timelineAt))
        : vs.length - 1;
      const label = document.createElement('span');
      label.className = 'vs-timeline-label';
      const fmt = (e: number) => {
        const d = new Date(e * 1000);
        const p = (n: number) => String(n).padStart(2, '0');
        return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
      };
      const paint = (i: number) => {
        const v = vs[i];
        const live = this.timelineAt === null;
        label.textContent = `${fmt(v.epoch)}${v.door ? ' · ' + v.door : ''}` +
          (live ? ' · live' : ` · ${i + 1}/${vs.length}`);
      };
      const go = async (i: number, live: boolean) => {
        this.timelineAt = live ? null : vs[i].epoch;
        slider.value = String(i);
        paint(i);
        const base = `files/checkpoints/${encodeURIComponent(this.currentSnapshotName!)}`;
        const url = live ? base : `${base}?at=${vs[i].epoch}`;
        try {
          const r = await fetch(url, { cache: 'no-store' });
          if (r.ok) await this.loadSnapshot(await r.json(), this.currentSnapshotName!);
          void this.renderTimeline();             // re-attach (loadSnapshot rebuilt the host)
        } catch { /* the status line already reports fetch errors */ }
      };
      const btn = (txt: string, tip: string, onClick: () => void) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'vs-timeline-btn';
        b.textContent = txt;
        b.title = tip;
        b.addEventListener('click', (e) => { e.stopPropagation(); onClick(); });
        return b;
      };
      const slider = document.createElement('input');
      slider.type = 'range';
      slider.min = '0';
      slider.max = String(vs.length - 1);
      slider.step = '1';
      slider.value = String(idx);
      slider.className = 'vs-timeline-slider';
      slider.addEventListener('click', (e) => e.stopPropagation());
      slider.addEventListener('change', () => { void go(Number(slider.value), false); });
      strip.append(
        btn('◀', 'Previous scan', () => { if (Number(slider.value) > 0) void go(Number(slider.value) - 1, false); }),
        slider,
        btn('▶', 'Next scan', () => { if (Number(slider.value) < vs.length - 1) void go(Number(slider.value) + 1, false); }),
        btn('●', 'Back to live (the track\'s latest scan)', () => { void go(vs.length - 1, true); }),
        label,
      );
      paint(idx);
      this.host.appendChild(strip);
      this.timelineEl = strip;
    } catch { /* a strip that cannot load must not take the cell down */ }
  }

  /** Hidden tab: stop drawing (loads keep going); shown: draw again. */
  setVisible(visible: boolean): void {
    if (!this.viewer) return;
    if (visible) this.viewer.start();
    else this.viewer.stop();
  }

  dispose(): void {
    this.host.removeEventListener('dragover', this.onDragOver);
    this.host.removeEventListener('dragleave', this.onDragLeave);
    this.host.removeEventListener('drop', this.onDrop);
    this.dropEl.removeEventListener('click', this.onDropClick);
    this.clearViewer();
    this.host.classList.remove('widget-viewer-snapshot', 'loaded', 'vs-drag');
    this.host.innerHTML = '';
  }
}

export const viewerSnapshotWidget: DashboardWidget = {
  id: 'viewer-snapshot',
  label: 'Viewer snapshot',
  description: 'Loads a saved 3D viewer snapshot (IFC / point cloud / splat / map).',
  mount(host, config, ctx) {
    return new ViewerSnapshotInstance(host, (config ?? {}) as ViewerSnapshotConfig, ctx);
  },
};
